'use client'

import { use, useState } from 'react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'
import { MEDIUMS } from '@/lib/ontology'
import { getArtistInitials, getArtistColorVar } from '@/lib/artistAvatar'

// Sample data for mediums
const mediumData: Record<string, {
  volume: string
  lots: string
  change: number
  avgHammer: string
  description: string
  topArtist: string
}> = {
  'paintings': {
    volume: '$8.2B',
    lots: '312K',
    change: 9.4,
    avgHammer: '$26K',
    description: 'Oil, acrylic, tempera, encaustic, and other painted works on canvas, panel, or paper.',
    topArtist: 'Jean-Michel Basquiat',
  },
  'sculpture': {
    volume: '$2.1B',
    lots: '89K',
    change: 7.2,
    avgHammer: '$24K',
    description: 'Three-dimensional works in bronze, marble, wood, steel, found objects, and installations.',
    topArtist: 'Alberto Giacometti',
  },
  'prints': {
    volume: '$1.4B',
    lots: '156K',
    change: 5.8,
    avgHammer: '$9K',
    description: 'Editioned works including lithographs, screenprints, etchings, woodcuts, and monotypes.',
    topArtist: 'Andy Warhol',
  },
  'photography': {
    volume: '$890M',
    lots: '78K',
    change: 12.4,
    avgHammer: '$11K',
    description: 'Fine art photography from 19th century processes to contemporary digital prints.',
    topArtist: 'Andreas Gursky',
  },
  'drawings': {
    volume: '$380M',
    lots: '124K',
    change: 3.2,
    avgHammer: '$3K',
    description: 'Works on paper including pencil, charcoal, pastel, ink, and watercolor.',
    topArtist: 'Pablo Picasso',
  },
  'mixed-media': {
    volume: '$180M',
    lots: '45K',
    change: 18.6,
    avgHammer: '$4K',
    description: 'Collage, assemblage, multimedia installations, and works combining multiple techniques.',
    topArtist: 'Robert Rauschenberg',
  },
}

const topArtistsByMedium: Record<string, { name: string; slug: string; volume: string; change: number; tier: string }[]> = {
  'paintings': [
    { name: 'Jean-Michel Basquiat', slug: 'jean-michel-basquiat', volume: '$285M', change: 12.1, tier: 'Blue-chip' },
    { name: 'Gerhard Richter', slug: 'gerhard-richter', volume: '$156M', change: -2.3, tier: 'Blue-chip' },
    { name: 'Yayoi Kusama', slug: 'yayoi-kusama', volume: '$142M', change: 18.4, tier: 'Blue-chip' },
    { name: 'David Hockney', slug: 'david-hockney', volume: '$128M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Yoshitomo Nara', slug: 'yoshitomo-nara', volume: '$72M', change: 24.7, tier: 'Blue-chip' },
  ],
  'sculpture': [
    { name: 'Alberto Giacometti', slug: 'alberto-giacometti', volume: '$98M', change: 5.4, tier: 'Blue-chip' },
    { name: 'Jeff Koons', slug: 'jeff-koons', volume: '$86M', change: -8.2, tier: 'Blue-chip' },
    { name: 'Constantin Brancusi', slug: 'constantin-brancusi', volume: '$72M', change: 12.1, tier: 'Blue-chip' },
    { name: 'Henry Moore', slug: 'henry-moore', volume: '$45M', change: 2.3, tier: 'Blue-chip' },
    { name: 'Anish Kapoor', slug: 'anish-kapoor', volume: '$38M', change: 8.5, tier: 'Blue-chip' },
  ],
  'prints': [
    { name: 'Andy Warhol', slug: 'andy-warhol', volume: '$124M', change: 6.8, tier: 'Blue-chip' },
    { name: 'Pablo Picasso', slug: 'pablo-picasso', volume: '$89M', change: 3.2, tier: 'Blue-chip' },
    { name: 'Roy Lichtenstein', slug: 'roy-lichtenstein', volume: '$56M', change: 4.5, tier: 'Blue-chip' },
    { name: 'Banksy', slug: 'banksy', volume: '$48M', change: 15.2, tier: 'Blue-chip' },
    { name: 'Kaws', slug: 'kaws', volume: '$32M', change: -12.4, tier: 'Blue-chip' },
  ],
  'photography': [
    { name: 'Andreas Gursky', slug: 'andreas-gursky', volume: '$42M', change: 8.4, tier: 'Blue-chip' },
    { name: 'Cindy Sherman', slug: 'cindy-sherman', volume: '$28M', change: 5.2, tier: 'Blue-chip' },
    { name: 'Richard Avedon', slug: 'richard-avedon', volume: '$18M', change: 12.8, tier: 'Blue-chip' },
    { name: 'Helmut Newton', slug: 'helmut-newton', volume: '$14M', change: 6.4, tier: 'Blue-chip' },
    { name: 'Wolfgang Tillmans', slug: 'wolfgang-tillmans', volume: '$12M', change: 22.1, tier: 'Blue-chip' },
  ],
  'drawings': [
    { name: 'Pablo Picasso', slug: 'pablo-picasso', volume: '$45M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Egon Schiele', slug: 'egon-schiele', volume: '$38M', change: 8.5, tier: 'Blue-chip' },
    { name: 'Henri Matisse', slug: 'henri-matisse', volume: '$32M', change: 2.1, tier: 'Blue-chip' },
    { name: 'Jean-Michel Basquiat', slug: 'jean-michel-basquiat', volume: '$28M', change: 15.4, tier: 'Blue-chip' },
    { name: 'Gustav Klimt', slug: 'gustav-klimt', volume: '$24M', change: 6.8, tier: 'Blue-chip' },
  ],
  'mixed-media': [
    { name: 'Robert Rauschenberg', slug: 'robert-rauschenberg', volume: '$32M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Jasper Johns', slug: 'jasper-johns', volume: '$28M', change: 2.8, tier: 'Blue-chip' },
    { name: 'Joseph Cornell', slug: 'joseph-cornell', volume: '$18M', change: 8.4, tier: 'Blue-chip' },
    { name: 'Anselm Kiefer', slug: 'anselm-kiefer', volume: '$15M', change: 12.1, tier: 'Blue-chip' },
    { name: 'Mark Bradford', slug: 'mark-bradford', volume: '$12M', change: 18.5, tier: 'Blue-chip' },
  ],
}

const periodBreakdown = [
  { period: 'Contemporary', volume: '$4.2B', share: 42, change: 12.4 },
  { period: 'Modern', volume: '$2.8B', share: 28, change: 5.1 },
  { period: 'Post-War', volume: '$1.8B', share: 18, change: 8.2 },
  { period: 'Old Masters', volume: '$1.2B', share: 12, change: 2.4 },
]

// Geographic insights
const geographicInsights = [
  { region: 'Americas', volume: '$3.8B', share: 38, change: 10.2, topCity: 'New York' },
  { region: 'Europe', volume: '$3.2B', share: 32, change: 5.8, topCity: 'London' },
  { region: 'Asia-Pacific', volume: '$2.4B', share: 24, change: 18.4, topCity: 'Hong Kong' },
  { region: 'Middle East', volume: '$0.6B', share: 6, change: 24.5, topCity: 'Dubai' },
]

// Market trends
const marketTrends = [
  { trend: 'Large-scale works commanding premium', change: 28, direction: 'up' as const },
  { trend: 'Growing institutional demand', change: 22, direction: 'up' as const },
  { trend: 'Asian collectors driving prices', change: 18, direction: 'up' as const },
  { trend: 'Emerging artist segment cooling', change: -8, direction: 'down' as const },
]

// Upcoming auctions
const upcomingAuctions = [
  { house: "Christie's", title: 'Post-War & Contemporary Art Evening Sale', date: 'Mar 15, 2026', estimate: '$120-180M', lots: 48, location: 'New York' },
  { house: "Sotheby's", title: 'Contemporary Art Day Sale', date: 'Mar 18, 2026', estimate: '$45-65M', lots: 124, location: 'London' },
  { house: 'Phillips', title: '20th Century & Contemporary Art', date: 'Mar 22, 2026', estimate: '$35-50M', lots: 86, location: 'Hong Kong' },
  { house: 'Bonhams', title: 'Modern & Contemporary Art', date: 'Apr 2, 2026', estimate: '$15-25M', lots: 92, location: 'Los Angeles' },
]

// Recent sales
const recentSales = [
  { artist: 'Jean-Michel Basquiat', work: 'Untitled (Devil)', house: "Christie's", hammer: '$42.4M', estimate: '$35-45M', premium: '+18%', date: '2 days ago' },
  { artist: 'Gerhard Richter', work: 'Abstraktes Bild (648-3)', house: "Sotheby's", hammer: '$28.2M', estimate: '$25-35M', premium: '+12%', date: '3 days ago' },
  { artist: 'Yayoi Kusama', work: 'Infinity Nets (QRTW)', house: 'Phillips', hammer: '$8.9M', estimate: '$6-8M', premium: '+24%', date: '5 days ago' },
  { artist: 'David Hockney', work: 'Portrait of an Artist', house: "Christie's", hammer: '$18.4M', estimate: '$15-20M', premium: '+8%', date: '1 week ago' },
  { artist: 'Banksy', work: 'Girl with Balloon', house: "Sotheby's", hammer: '$12.1M', estimate: '$10-15M', premium: '+14%', date: '1 week ago' },
]

// Artist avatar component
function ArtistAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = getArtistInitials(name)
  const colorVar = getArtistColorVar(name)

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-base',
  }

  return (
    <div
      className={`${sizeClasses[size]} rounded-lg flex items-center justify-center font-medium text-white flex-shrink-0`}
      style={{ background: `var(${colorVar})` }}
    >
      {initials}
    </div>
  )
}

// Medium icons
function MediumIcon({ medium }: { medium: string }) {
  const iconClass = "w-12 h-12 text-accent-primary"

  switch (medium) {
    case 'paintings':
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="4" y="8" width="24" height="16" rx="1" />
          <line x1="4" y1="20" x2="12" y2="14" />
          <line x1="12" y1="14" x2="18" y2="18" />
          <line x1="18" y1="18" x2="28" y2="12" />
          <circle cx="22" cy="12" r="2" />
        </svg>
      )
    case 'sculpture':
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="16" cy="10" r="6" />
          <ellipse cx="16" cy="24" rx="8" ry="4" />
          <line x1="16" y1="16" x2="16" y2="20" />
        </svg>
      )
    case 'prints':
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="8" y="4" width="16" height="20" rx="1" />
          <rect x="10" y="6" width="12" height="14" rx="0.5" />
          <line x1="10" y1="26" x2="22" y2="26" />
          <line x1="12" y1="28" x2="20" y2="28" />
        </svg>
      )
    case 'photography':
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="6" width="14" height="16" rx="1" transform="rotate(-8 6 6)" />
          <rect x="12" y="8" width="14" height="16" rx="1" />
          <rect x="14" y="10" width="10" height="10" rx="0.5" />
          <line x1="14" y1="22" x2="24" y2="22" />
        </svg>
      )
    case 'drawings':
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M6 20 Q10 8, 16 16 T26 12" strokeLinecap="round" />
          <path d="M8 24 Q12 18, 18 22 T24 18" strokeLinecap="round" opacity="0.5" />
          <circle cx="26" cy="10" r="2" fill="currentColor" opacity="0.3" />
        </svg>
      )
    case 'mixed-media':
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="10" width="12" height="12" rx="1" />
          <circle cx="22" cy="14" r="6" />
          <polygon points="16,6 20,14 12,14" />
        </svg>
      )
    default:
      return null
  }
}

function ShareButton({ title }: { title?: string }) {
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const shareUrl = window.location.href
    const shareTitle = title || 'Medium - salle.art'

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl })
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      onClick={handleShare}
      className="pill text-sm flex items-center gap-2"
      style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
    >
      {copied ? (
        <>
          <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </>
      )}
    </button>
  )
}

export default function MediumPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params)
  const { canExport } = useAuth()

  // Find medium from ontology
  const mediumInfo = MEDIUMS.find(m => m.id === id)
  const mediumName = mediumInfo?.name || id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

  const data = mediumData[id] || {
    volume: '$500M',
    lots: '25K',
    change: 5.0,
    avgHammer: '$20K',
    description: mediumInfo?.description || 'Fine art in this medium.',
    topArtist: 'Various Artists',
  }

  const topArtists = topArtistsByMedium[id] || topArtistsByMedium['paintings']

  // AI outlook
  const aiOutlook = {
    summary: `The ${mediumName} market shows ${data.change >= 0 ? 'positive' : 'negative'} momentum with ${Math.abs(data.change)}% ${data.change >= 0 ? 'growth' : 'decline'} YoY. ${data.change >= 10 ? 'Strong collector demand is driving prices higher across all tiers.' : data.change >= 0 ? 'Steady interest continues with selective buying in quality works.' : 'Market cooling presents potential opportunities for value buyers.'}`,
    recommendation: data.change >= 0
      ? `Focus on authenticated works with strong provenance. ${mediumName} by established artists remain liquid assets.`
      : `Quality over quantity - blue-chip ${mediumName.toLowerCase()} are holding value better than speculative positions.`,
  }

  return (
    <div className="px-4 md:px-6 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-tertiary mb-6">
        <Link href="/app/market" className="hover:text-text-secondary transition-colors">
          Market
        </Link>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-secondary">Medium</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-primary">{mediumName}</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-page border border-border-subtle">
              <MediumIcon medium={id} />
            </div>
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-light mb-2">{mediumName}</h1>
              <p className="text-text-secondary max-w-xl">{data.description}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <ShareButton title={`${mediumName} - salle.art`} />
            <button
              className="pill text-sm flex items-center gap-2"
              style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Set Alert
            </button>
            <TierGate feature="export" showPrompt={false}>
              <button
                className="pill text-sm flex items-center gap-2"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
            </TierGate>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Total Volume</p>
          <p className="font-display text-2xl font-light">{data.volume}</p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Total Lots</p>
          <p className="font-display text-2xl font-light">{data.lots}</p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">YoY Change</p>
          <p
            className="font-display text-2xl font-light"
            style={{ color: data.change >= 0 ? 'var(--positive)' : 'var(--negative)' }}
          >
            {data.change >= 0 ? '+' : ''}{data.change}%
          </p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Avg. Hammer</p>
          <p className="font-display text-2xl font-light">{data.avgHammer}</p>
        </div>
      </div>

      {/* AI Outlook */}
      <div className="card p-5 mb-8 border-l-4" style={{ borderLeftColor: 'var(--accent-primary)' }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium mb-2">AI Market Outlook</h3>
            <p className="text-sm text-text-secondary mb-3">{aiOutlook.summary}</p>
            <div className="bg-page rounded-lg p-3">
              <p className="text-xs text-text-tertiary mb-1">Recommendation</p>
              <p className="text-sm text-text-primary">{aiOutlook.recommendation}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Period breakdown */}
      <div className="card p-6 mb-8">
        <h2 className="font-medium mb-4">By Period</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {periodBreakdown.map((period) => (
            <Link
              key={period.period}
              href={`/app/market/${period.period.toLowerCase()}`}
              className="bg-page rounded-xl p-4 hover:bg-card transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{period.period}</span>
                <span className={`text-xs tabular-nums ${period.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {period.change >= 0 ? '+' : ''}{period.change}%
                </span>
              </div>
              <p className="text-lg font-display font-light mb-1">{period.volume}</p>
              <div className="w-full bg-card rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full"
                  style={{ width: `${period.share}%`, background: 'var(--accent-primary)' }}
                />
              </div>
              <p className="text-xs text-text-tertiary mt-1">{period.share}% share</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Top artists */}
      <div className="card p-6 mb-8">
        <h2 className="font-medium mb-4">Top Artists in {mediumName}</h2>
        <div className="space-y-3">
          {topArtists.map((artist, i) => (
            <Link
              key={artist.slug}
              href={`/app/artists/${artist.slug}`}
              className="flex items-center justify-between py-3 border-b border-border-subtle last:border-0 hover:bg-page -mx-3 px-3 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-text-tertiary text-sm w-6">{i + 1}</span>
                <div>
                  <p className="font-medium text-sm">{artist.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    artist.tier === 'Blue-chip'
                      ? 'bg-accent-primary/10 text-accent-primary'
                      : artist.tier === 'Mid-market'
                      ? 'bg-accent-warm/10 text-accent-warm'
                      : 'bg-positive/10 text-positive'
                  }`}>
                    {artist.tier}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-sm tabular-nums">{artist.volume}</p>
                <p className={`text-xs tabular-nums ${artist.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {artist.change >= 0 ? '+' : ''}{artist.change}%
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Geographic Insights */}
      <div className="card p-6 mb-8">
        <h2 className="font-medium mb-4">Geographic Insights</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {geographicInsights.map((geo) => (
            <div key={geo.region} className="bg-page rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{geo.region}</span>
                <span className={`text-xs tabular-nums ${geo.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {geo.change >= 0 ? '+' : ''}{geo.change}%
                </span>
              </div>
              <p className="text-lg font-display font-light mb-1">{geo.volume}</p>
              <div className="w-full bg-card rounded-full h-1.5 mb-2">
                <div
                  className="h-1.5 rounded-full"
                  style={{ width: `${geo.share}%`, background: 'var(--accent-primary)' }}
                />
              </div>
              <p className="text-xs text-text-tertiary">{geo.share}% · Top: {geo.topCity}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Market Trends */}
      <div className="card p-6 mb-8">
        <h2 className="font-medium mb-4">Market Trends</h2>
        <div className="space-y-3">
          {marketTrends.map((trend, i) => (
            <div key={i} className="flex items-center justify-between py-3 border-b border-border-subtle last:border-0">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  trend.direction === 'up' ? 'bg-positive/10' : 'bg-negative/10'
                }`}>
                  <svg
                    className={`w-4 h-4 ${trend.direction === 'up' ? 'text-positive' : 'text-negative rotate-180'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                </div>
                <span className="text-sm">{trend.trend}</span>
              </div>
              <span className={`text-sm font-medium tabular-nums ${
                trend.change >= 0 ? 'text-positive' : 'text-negative'
              }`}>
                {trend.change >= 0 ? '+' : ''}{trend.change}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Auctions */}
      <div className="card p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium">Upcoming Auctions</h2>
          <Link href="/app/market" className="text-sm text-accent-primary hover:underline">View all</Link>
        </div>
        <div className="space-y-4">
          {upcomingAuctions.map((auction, i) => (
            <div key={i} className="flex items-start justify-between py-3 border-b border-border-subtle last:border-0">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent-primary/10 text-accent-primary font-medium">
                    {auction.house}
                  </span>
                  <span className="text-xs text-text-tertiary">{auction.location}</span>
                </div>
                <p className="font-medium text-sm mb-1">{auction.title}</p>
                <p className="text-xs text-text-tertiary">{auction.lots} lots · Est. {auction.estimate}</p>
              </div>
              <div className="text-right ml-4">
                <p className="text-sm font-medium">{auction.date}</p>
                <button className="text-xs text-accent-primary hover:underline mt-1">Set reminder</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Sales */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium">Recent Sales</h2>
          <Link href="/app/market" className="text-sm text-accent-primary hover:underline">View all</Link>
        </div>
        <div className="space-y-4">
          {recentSales.map((sale, i) => (
            <div key={i} className="flex items-center gap-4 py-3 border-b border-border-subtle last:border-0">
              <ArtistAvatar name={sale.artist} size="lg" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{sale.artist}</p>
                <p className="text-xs text-text-secondary truncate">{sale.work}</p>
                <p className="text-xs text-text-tertiary">{sale.house} · {sale.date}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-medium text-sm tabular-nums">{sale.hammer}</p>
                <p className="text-xs text-text-tertiary">Est. {sale.estimate}</p>
                <p className="text-xs text-positive tabular-nums">{sale.premium}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
